import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Skeleton from "@mui/material/Skeleton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Material Dashboard 2 React example components
import DashboardLayout from "layouts/DashboardLayout";
import DashboardNavbar from "navbars/DashboardNavbar";

// Overview page components
import TabSection from "./components/TabSection";
import FileState from "components/FileState";

import { useNotification } from "../../../context/NotificationContext";
import expedientesService from "../../../services/expedientesService";
import {resumeJurisdiction} from "../../../services/common";
import { CardActions, CardContent, Collapse, IconButton } from "@mui/material";
import { ContentCopy, EditNote, FolderSpecialOutlined, WorkOutlineOutlined } from "@mui/icons-material";


const ExpedienteDetalle = () => {
  let { id } = useParams();
  id = Number(id);
  const [expediente, setExpediente] = useState(null);
  const [title, setTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchExpedientes() {
      setLoading(true);
      const data = await expedientesService.getExpedienteById(id);
      if (data.error) {
        showNotification(data.message, "error");
      } else {
        setExpediente(data);
        const jurisdiccion = resumeJurisdiction(data.jurisdiccion);
        const numero = data.numero;
        const anio = data.anio;
        const caratula = data.caratula;
        document.title = `${jurisdiccion} ${numero}/${anio} ${caratula}`;

        if (data.cuaderno) {
          setTitle(
            resumeJurisdiction(data.jurisdiccion) +
              " " +
              data.numero +
              " / " +
              data.anio +
              " / " +
              data.cuaderno
          );
        } else {
          setTitle(resumeJurisdiction(data.jurisdiccion) + " " + data.numero + " / " + data.anio);
        }
      }
      setLoading(false);
    }
    fetchExpedientes();
    return () => {
      document.title = "Gestor de Expedientes";
    };
  }, [id]);

  const [expanded, setExpanded] = useState(false);
  const [rotate, setRotate] = useState("0");

  const handleExpandClick = () => {
    setExpanded(!expanded);
    if (rotate === "0") {
      setRotate("180");
    } else {
      setRotate("0");
    }
  };

  const handleClick = (text) => {
    if (document.hasFocus()) {
      navigator.clipboard.writeText(text);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar title="Detalle de expediente" />
        <Card>
          <CardContent sx={{ "&:last-child": { pb: 0 }, paddingBottom: 0 }}>
            <MDBox pt={1} />
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Skeleton variant="text" width={220} height={36} />
              </Grid>
              <Grid item>
                <Skeleton variant="rounded" width={80} height={24} />
              </Grid>
            </Grid>
            <Skeleton variant="text" width="60%" height={28} sx={{ mt: 0.5 }} />
          </CardContent>
          <CardActions disableSpacing sx={{ paddingLeft: 2 }}>
            <Skeleton variant="circular" width={36} height={36} />
            <Skeleton variant="circular" width={36} height={36} sx={{ ml: 1 }} />
            <Skeleton variant="circular" width={36} height={36} sx={{ ml: 1 }} />
          </CardActions>
        </Card>
        <MDBox pt={2} pb={0}>
          <Grid item xs={12}>
            <MDBox position="relative" mb={5}>
              <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 1, mt: 2 }} />
            </MDBox>
          </Grid>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (!expediente) {
    return (
      <DashboardLayout>
        <DashboardNavbar title="Detalle de expediente" />
        <MDTypography variant="h6" color="text">
          No hay información disponible.
        </MDTypography>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar title="Detalle de expediente" />
      <Card>
        <CardContent sx={{ "&:last-child": { pb: 0 }, paddingBottom: 0 }}>
          <MDBox pt={1} />
          <Grid container spacing={2}>
            <Grid item>
              <MDTypography variant="h5" fontWeight="medium">
                {title}
              </MDTypography>
            </Grid>
            <Grid item>
              <MDBox mt={-0.5}>
                <FileState id={id} state={expediente.estado}></FileState>
              </MDBox>
            </Grid>
          </Grid>

          <MDBox px={0} pt={0}>
            <MDTypography variant="h6" fontWeight="medium">
              {expediente.caratula}
            <IconButton
              size="small"
              disableRipple
              color="inherit"
              sx={{fontSize:"0.8rem", pt:0, alignContent:"flex-start"}}
              onClick={() => handleClick(title + " " + expediente.caratula)}
            >
              <ContentCopy color="text" sx={{ mr: 2}}></ContentCopy>
            </IconButton>
            </MDTypography>
          </MDBox>
        </CardContent>
        <CardActions disableSpacing sx={{ paddingLeft: 2 }}>
          <IconButton
            onClick={() => navigate("/file/edit/" + id)}
            color="info"
            sx={{
              transition: "0.3s",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <EditNote />
          </IconButton>
          {expediente.link_expediente ? (
            <IconButton
              onClick={() => window.open(expediente.link_expediente, "_blank")}
              color="info"
              sx={{
                transition: "0.3s",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <WorkOutlineOutlined />
            </IconButton>
          ) : (
            ""
          )}
          {expediente.link_repositorio ? (
            <IconButton
              onClick={() => window.open(expediente.link_repositorio, "_blank")}
              color="info"
              sx={{
                transition: "0.3s",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <FolderSpecialOutlined />
            </IconButton>
          ) : (
            ""
          )}
          <IconButton
            onClick={handleExpandClick}
            sx={{
              transform: "rotate(" + rotate + "deg)",
              cursor: "pointer",
              transition: "0.3s",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </CardActions>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <CardContent>
            <MDBox px={0} mt={0.5}>
              <MDTypography
                mt={-1}
                display="block"
                variant="button"
                fontWeight="regular"
                color="text"
              >
                {expediente.jurisdiccion}
              </MDTypography>
              <MDTypography display="block" variant="button" fontWeight="regular" color="text">
                {expediente.dependencia}
              </MDTypography>
              <MDTypography
                mt={1}
                display="flex"
                variant="button"
                fontWeight="regular"
                color="text"
              >
                Creación: {expediente.fecha_creacion}
              </MDTypography>
              <MDTypography display="flex" variant="button" fontWeight="regular" color="text">
                Sorteo: {expediente.fecha_sorteo}
              </MDTypography>
              <MDTypography display="flex" variant="button" fontWeight="regular" color="text">
                Notificación: {expediente.fecha_notificacion}
              </MDTypography>
              <MDTypography display="flex" variant="button" fontWeight="regular" color="text">
                Aceptación: {expediente.fecha_aceptacion}
              </MDTypography>
            </MDBox>
          </CardContent>
        </Collapse>
      </Card>

      <MDBox pt={2} pb={0}>
        <Grid item xs={12}>
          <MDBox position="relative" mb={5}>
            <TabSection expedienteId={id} />
          </MDBox>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
};

export default ExpedienteDetalle;
